import { useState, useEffect, useRef } from 'react';
import {
  CloudArrowUpIcon,
  DocumentIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  LinkIcon,
  FolderIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { evidenceAPI, tasksAPI } from '../../services/api';

// File type icons mapping
const getFileIcon = (fileType) => {
  const iconMap = {
    '.pdf': 'text-red-500',
    '.doc': 'text-blue-500',
    '.docx': 'text-blue-500',
    '.xls': 'text-green-500',
    '.xlsx': 'text-green-500',
    '.png': 'text-purple-500',
    '.jpg': 'text-purple-500',
    '.jpeg': 'text-purple-500',
    '.gif': 'text-purple-500',
    '.txt': 'text-gray-500',
    '.csv': 'text-green-500',
  };
  return iconMap[fileType?.toLowerCase()] || 'text-gray-400';
};

export default function EvidencePage() {
  const [evidence, setEvidence] = useState([]);
  const [storage, setStorage] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, linked, unlinked
  const [linkModalOpen, setLinkModalOpen] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter === 'linked') params.linked = 'true';
      if (filter === 'unlinked') params.linked = 'false';

      const [evidenceRes, storageRes, tasksRes] = await Promise.all([
        evidenceAPI.getEvidence(params),
        evidenceAPI.getStorageUsage(),
        tasksAPI.getTasks(),
      ]);

      setEvidence(evidenceRes.data.data || []);
      setStorage(storageRes.data.data || null);
      setTasks(tasksRes.data.data || []);
    } catch (err) {
      setError('Failed to load evidence');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files?.length > 0) {
      handleUpload(files[0]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files?.length > 0) {
      handleUpload(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('File size exceeds 10MB limit');
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);

      await evidenceAPI.upload(file);
      await fetchData();

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError(err.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = (id) => {
    // Open download in new tab with auth token
    const token = localStorage.getItem('token');
    window.open(
      `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/evidence/${id}/download?token=${token}`,
      '_blank'
    );
  };

  const handleDelete = async (id) => {
    try {
      await evidenceAPI.delete(id);
      setDeleteConfirm(null);
      await fetchData();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleLinkToTask = async (evidenceId, taskId) => {
    try {
      await evidenceAPI.linkToTask(evidenceId, taskId || null);
      setLinkModalOpen(null);
      await fetchData();
    } catch (err) {
      console.error('Link error:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading evidence..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <Button onClick={fetchData} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Evidence Locker</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">
          Upload and manage evidence documents for your Cyber Essentials certification
        </p>
      </div>

      {/* Storage Usage Card */}
      {storage && (
        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Storage Used
              </span>
              <span className="text-sm text-slate-600 dark:text-slate-400">
                {storage.usedFormatted} / {storage.limitFormatted}
              </span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  storage.percentage > 90
                    ? 'bg-red-500'
                    : storage.percentage > 70
                    ? 'bg-yellow-500'
                    : 'bg-blue-500'
                }`}
                style={{ width: `${Math.min(storage.percentage, 100)}%` }}
              />
            </div>
            {storage.percentage > 90 && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                <ExclamationTriangleIcon className="h-4 w-4" />
                Storage almost full. Consider upgrading your plan.
              </p>
            )}
          </div>
        </Card>
      )}

      {/* Upload Area */}
      <Card>
        <div className="p-6">
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              dragActive
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx"
            />

            {uploading ? (
              <LoadingSpinner size="md" text="Uploading..." />
            ) : (
              <>
                <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-slate-700 dark:text-slate-300 font-medium">
                  Drop files here or click to upload
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  PDF, images, Word, Excel, CSV (max 10MB)
                </p>
              </>
            )}
          </div>

          {uploadError && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{uploadError}</p>
            </div>
          )}
        </div>
      </Card>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {[
          { value: 'all', label: 'All Files' },
          { value: 'linked', label: 'Linked to Tasks' },
          { value: 'unlinked', label: 'Unlinked' },
        ].map((tab) => (
          <button
            key={tab.value}
            onClick={() => setFilter(tab.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === tab.value
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                : 'text-slate-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Evidence List */}
      <Card padding={false}>
        {evidence.length === 0 ? (
          <div className="p-12 text-center">
            <FolderIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-white">No evidence yet</h3>
            <p className="mt-1 text-slate-600 dark:text-slate-400">
              Upload your first evidence document to get started
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {evidence.map((item) => (
              <div key={item.id} className="p-4 hover:bg-gray-50 dark:hover:bg-slate-800/50">
                <div className="flex items-center gap-4">
                  {/* File icon */}
                  <div className={`flex-shrink-0 ${getFileIcon(item.fileType)}`}>
                    <DocumentIcon className="h-10 w-10" />
                  </div>

                  {/* File info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-slate-900 dark:text-white truncate">
                      {item.originalName}
                    </h4>
                    <div className="flex items-center gap-3 mt-1 text-sm text-slate-500 dark:text-slate-400">
                      <span>{item.fileSizeFormatted}</span>
                      <span>·</span>
                      <span>{new Date(item.createdAt).toLocaleDateString()}</span>
                      {item.task && (
                        <>
                          <span>·</span>
                          <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                            <LinkIcon className="h-3 w-3" />
                            {item.task.title}
                          </span>
                        </>
                      )}
                    </div>
                    {item.description && (
                      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 truncate">
                        {item.description}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleDownload(item.id)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title="Download"
                    >
                      <ArrowDownTrayIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setLinkModalOpen(item)}
                      className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                      title="Link to task"
                    >
                      <LinkIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(item)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Link to Task Modal */}
      {linkModalOpen && (
        <LinkTaskModal
          evidence={linkModalOpen}
          tasks={tasks}
          onLink={handleLinkToTask}
          onClose={() => setLinkModalOpen(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <DeleteConfirmModal
          evidence={deleteConfirm}
          onConfirm={() => handleDelete(deleteConfirm.id)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
}

// Link to Task Modal
function LinkTaskModal({ evidence, tasks, onLink, onClose }) {
  const [selectedTask, setSelectedTask] = useState(evidence.taskId || '');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
            Link to Task
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Link "{evidence.originalName}" to a Cyber Essentials task:
          </p>

          <select
            value={selectedTask}
            onChange={(e) => setSelectedTask(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-slate-900 dark:text-white mb-6"
          >
            <option value="">No task (unlinked)</option>
            {tasks.map((task) => (
              <option key={task.id} value={task.id}>
                {task.definition?.title}
              </option>
            ))}
          </select>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => onLink(evidence.id, selectedTask || null)}>Save</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Delete Confirmation Modal
function DeleteConfirmModal({ evidence, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
        <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-sm w-full p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
              <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Delete Evidence?
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">This cannot be undone.</p>
            </div>
          </div>

          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
            Are you sure you want to delete "{evidence.originalName}"?
          </p>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              className="bg-red-600 hover:bg-red-700 text-white border-red-600"
            >
              Delete
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
